/**
 * Kleiner Markdown-Leser für die Antworten der KI (#1336).
 *
 * Bewusst kein Paket: gebraucht wird die Teilmenge, die Sprachmodelle
 * tatsächlich schicken — Überschriften, Aufzählungen, Fettschrift, Code. Ein
 * vollständiger Markdown-Übersetzer wäre dafür ein schwerer Kauf, und das
 * letzte nachgezogene Paket hat den Abhängigkeitsbaum durcheinandergebracht.
 *
 * Reine Funktionen ohne React, damit sie ohne Rendering prüfbar sind. Die
 * Ausgabe wird als React-Elemente gebaut, nie als HTML eingesetzt — damit ist
 * eingeschleustes Markup von vornherein kein Thema.
 */

export type InlinePart =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'code'; text: string };

export type MarkdownBlock =
  | { kind: 'paragraph'; parts: InlinePart[] }
  | { kind: 'heading'; level: 1 | 2 | 3; parts: InlinePart[] }
  | { kind: 'bullets'; items: InlinePart[][] }
  | { kind: 'numbers'; items: InlinePart[][] }
  | { kind: 'code'; text: string }
  | { kind: 'table'; header: InlinePart[][]; rows: InlinePart[][][] }
  | { kind: 'rule' };

/** `**fett**`, `*kursiv*`, `_kursiv_` und `` `code` `` in Teile zerlegen. */
export const parseInline = (line: string): InlinePart[] => {
  const parts: InlinePart[] = [];
  // Reihenfolge zählt: `**` muss vor `*` greifen, sonst wird Fettschrift als
  // zweimal kursiv gelesen.
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*|_[^_\n]+_)/g;

  let last = 0;
  let match = pattern.exec(line);

  while (match !== null) {
    if (match.index > last) {
      parts.push({ kind: 'text', text: line.slice(last, match.index) });
    }

    const token = match[0];

    if (token.startsWith('**')) {
      parts.push({ kind: 'bold', text: token.slice(2, -2) });
    } else if (token.startsWith('`')) {
      parts.push({ kind: 'code', text: token.slice(1, -1) });
    } else {
      parts.push({ kind: 'italic', text: token.slice(1, -1) });
    }

    last = match.index + token.length;
    match = pattern.exec(line);
  }

  if (last < line.length) {
    parts.push({ kind: 'text', text: line.slice(last) });
  }

  return parts.length > 0 ? parts : [{ kind: 'text', text: '' }];
};

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBER = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^(#{1,3})\s+(.*)$/;
const FENCE = /^\s*```/;
const RULE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const TABLE_ROW = /\|/;
// Trennzeile einer Tabelle: | --- | :--: | ---: |
const TABLE_DIVIDER = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

/** Eine Tabellenzeile in ihre Zellen zerlegen. */
const splitRow = (line: string): string[] => {
  const cells = line.trim().split('|');

  // Führendes und schließendes `|` erzeugen leere Randzellen — die gehören
  // nicht zur Tabelle. Zellen in der Mitte dürfen sehr wohl leer sein.
  if (cells.length > 0 && cells[0].trim() === '') {
    cells.shift();
  }

  if (cells.length > 0 && cells[cells.length - 1].trim() === '') {
    cells.pop();
  }

  return cells.map(cell => cell.trim());
};

/**
 * Text in Blöcke zerlegen.
 *
 * Aufeinanderfolgende Aufzählungszeilen werden zu **einem** Block: sonst
 * entstünde je Punkt eine eigene Liste, und der Abstand zwischen den Punkten
 * wäre größer als zwischen den Absätzen.
 */
export const parseMarkdown = (input: string): MarkdownBlock[] => {
  const lines = (input || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];

  let paragraph: string[] = [];
  let bullets: string[] = [];
  let numbers: string[] = [];
  let code: string[] | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', parts: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
  };

  const flushLists = () => {
    if (bullets.length > 0) {
      blocks.push({ kind: 'bullets', items: bullets.map(parseInline) });
      bullets = [];
    }

    if (numbers.length > 0) {
      blocks.push({ kind: 'numbers', items: numbers.map(parseInline) });
      numbers = [];
    }
  };

  const flushAll = () => {
    flushParagraph();
    flushLists();
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (FENCE.test(line)) {
      if (code === null) {
        flushAll();
        code = [];
      } else {
        blocks.push({ kind: 'code', text: code.join('\n') });
        code = null;
      }

      continue;
    }

    if (code !== null) {
      code.push(line);

      continue;
    }

    if (line.trim() === '') {
      flushAll();

      continue;
    }

    if (RULE.test(line)) {
      flushAll();
      blocks.push({ kind: 'rule' });

      continue;
    }

    // Eine Tabelle ist erst eine, wenn die Trennzeile folgt — sonst wäre jede
    // Zeile mit einem `|` darin plötzlich eine einspaltige Tabelle.
    if (
      TABLE_ROW.test(line) &&
      index + 1 < lines.length &&
      TABLE_DIVIDER.test(lines[index + 1]) &&
      TABLE_ROW.test(lines[index + 1])
    ) {
      flushAll();

      const header = splitRow(line).map(parseInline);
      const rows: InlinePart[][][] = [];
      let cursor = index + 2;

      while (cursor < lines.length && TABLE_ROW.test(lines[cursor])) {
        rows.push(splitRow(lines[cursor]).map(parseInline));
        cursor += 1;
      }

      blocks.push({ kind: 'table', header, rows });
      index = cursor - 1;

      continue;
    }

    const heading = HEADING.exec(line);

    if (heading) {
      flushAll();
      blocks.push({
        kind: 'heading',
        level: heading[1].length as 1 | 2 | 3,
        parts: parseInline(heading[2]),
      });

      continue;
    }

    const bullet = BULLET.exec(line);

    if (bullet) {
      flushParagraph();

      if (numbers.length > 0) {
        flushLists();
      }

      bullets.push(bullet[1]);

      continue;
    }

    const numbered = NUMBER.exec(line);

    if (numbered) {
      flushParagraph();

      if (bullets.length > 0) {
        flushLists();
      }

      numbers.push(numbered[1]);

      continue;
    }

    flushLists();
    paragraph.push(line.trim());
  }

  // Ein nicht geschlossener Code-Block darf den Rest nicht verschlucken.
  if (code !== null) {
    blocks.push({ kind: 'code', text: code.join('\n') });
  }

  flushAll();

  return blocks;
};
